import { addDays, parse, set } from "date-fns";
import {
  BookingData,
  BookingTool,
  TimeSlot,
} from "../../../AppDataProvider/AppDataProvider";
import {
  GetOrganisationsAvailabilityQueryVariables,
  useGetOrganisationsAvailabilityQuery,
} from "../../../GeneratedGraphQL";
import { requireBookingDataFields, toZonedISOString } from "../../../utils";

import { ApolloError } from "@apollo/client";
import { Mixpanel } from "../../../mixpanel";
import { PartRequired } from "../../../utils/types";

export type UseGetOrganisationsAvailabilityOpts = {
  bookingData: BookingData;
  bookingTool: BookingTool;
  filteredOrganisationIds: string[];
  queryByOrganisationTimeZone: boolean;
  selectedDate: string;
};

export type UseGetOrganisationsAvailabilityResult = {
  error?: ApolloError;
  loading: boolean;
  organisationsAvailability: {
    organisation: { _id: string; name: string };
    timeSlots: TimeSlot[];
  }[];
};

type UseGetOrganisationsAvailabilityBookingData = PartRequired<
  BookingData,
  "sessionId" | "timezone"
>;

export const useGetOrganisationsAvailability = (
  options: UseGetOrganisationsAvailabilityOpts,
): UseGetOrganisationsAvailabilityResult => {
  const {
    bookingData,
    bookingTool,
    filteredOrganisationIds,
    selectedDate,
    queryByOrganisationTimeZone,
  } = options;

  requireBookingDataFields<UseGetOrganisationsAvailabilityBookingData>(
    bookingData,
    ["sessionId", "timezone"],
  );

  const { timezone, sessionId } = bookingData;

  const parsedDate = parse(selectedDate, "yyyy-MM-dd", new Date());

  const startIntervalsDate = set(parsedDate, {
    hours: 0,
    minutes: 0,
    seconds: 0,
  });

  const endIntervalsDate = addDays(startIntervalsDate, 1);

  const variables: GetOrganisationsAvailabilityQueryVariables = {
    id: bookingTool._id,
    meta: { sessionId },
    organisationIds: filteredOrganisationIds,
    queryByOrganisationTimeZone,
    range: {
      end: toZonedISOString(endIntervalsDate, timezone),
      start: toZonedISOString(startIntervalsDate, timezone),
    },
    timezone: queryByOrganisationTimeZone ? undefined : timezone,
  };

  const { data, loading, error } = useGetOrganisationsAvailabilityQuery({
    errorPolicy: "all",
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    variables,
  });

  const organisationsAvailability = (
    data?.bookingTool?.organisationsAvailability ?? []
  ).map(({ organisation, intervals }) => ({
    organisation: {
      _id: organisation._id,
      name: organisation.name,
    },
    timeSlots:
      intervals.map((interval) => {
        if (interval?.__typename === "GroupBookingInterval") {
          return {
            interval: {
              __typename: interval.__typename,
              booking: {
                _id: interval.booking._id,
                end: interval.booking.end,
                remainingSpaces: interval.booking.remainingSpaces,
                start: interval.booking.start,
                ...(interval.booking.recurring && {
                  recurring: interval.booking.recurring,
                }),
                ...(interval.booking.price && {
                  price: interval.booking.price,
                }),
              },
              end: interval.end,
              remainingSpaces: interval.remainingSpaces,
              start: interval.start,
            },
          } as TimeSlot;
        }
        return { interval } as TimeSlot;
      }) ?? [],
  }));

  const totalTimeSlots = organisationsAvailability.reduce(
    (result, current) => (result += current.timeSlots.length),
    0,
  );
  if (!loading && !error && totalTimeSlots === 0) {
    Mixpanel.track({
      name: "No availability",
      props: {
        searchedDate:
          sessionStorage.getItem(`${sessionId}:selected-date`) ?? "unknown",
        searchedLocation:
          sessionStorage.getItem(`${sessionId}:location-search-query`) ??
          "unknown",
        searchedTime:
          sessionStorage.getItem(`${sessionId}:selected-time`) ?? "unknown",
        searchType: "LOCATION_AVAILABILITY",
      },
    });
  }

  return {
    error,
    loading,
    organisationsAvailability,
  };
};
